import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "../../../styles/icons/RefreshIcon";

export default function WhatsappTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
}) {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const SEARCH_ICON_BG =
    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.7549 14.255H14.9649L14.6849 13.985C15.6649 12.845 16.2549 11.365 16.2549 9.755C16.2549 6.165 13.3449 3.255 9.75488 3.255C6.16488 3.255 3.25488 6.165 3.25488 9.755C3.25488 13.345 6.16488 16.255 9.75488 16.255C11.3649 16.255 12.8449 15.665 13.9849 14.685L14.2549 14.965V15.755L19.2549 20.745L20.7449 19.255L15.7549 14.255ZM9.75488 14.255C7.26488 14.255 5.25488 12.245 5.25488 9.755C5.25488 7.26501 7.26488 5.255 9.75488 5.255C12.2449 5.255 14.2549 7.26501 14.2549 9.755C14.2549 12.245 12.2449 14.255 9.75488 14.255Z' fill='white'/%3E%3C/svg%3E\")";

  // Local state -----------------------------------------------------------
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(templates);
  const [selectedTpl, setSelectedTpl] = useState(null);

  // Helpers ----------------------------------------------------------------
  const matches = (tpl, q) =>
    [
      tpl.TemplateId ?? tpl.Id,
      tpl.TemplateName,
      tpl.MessageText ?? tpl.Content ?? tpl.HtmlBody,
    ].some((v) => String(v).toLowerCase().includes(q));

  // Effects ----------------------------------------------------------------
  useEffect(() => {
    setFiltered(templates.filter((t) => matches(t, search.toLowerCase())));
  }, [templates, search]);

  // -------------------------------------------------------- Render helpers
  const statusColor = (status) => {
    switch (status) {
      case 1:
        return "#38c172"; // Approved
      case 3:
        return "#ff4d4f"; // Rejected
      default:
        return "#8a8a8a";
    }
  };

  // ---------------------------------------------------------------- Handlers
  const handleSubmit = () => {
    if (selectedTpl) {
      setSelected(selectedTpl);
      setClosed();
    }
  };

  // ---------------------------------------------------------------- JSX
  return (
    <Dialog
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "905px",
          maxWidth: "905px",
          height: "829px",
          maxHeight: "829px",
          borderRadius: "7px",
          border: "1px solid rgba(255, 255, 255, 0.45)",
          bgcolor: COLOR_SURFACE,
          color: "#FFFFFF",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: COLOR_SURFACE,
        }}
      >
        {/* Search */}
        <Box
          sx={{
            px: "20px",
            py: "10px",
            borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
            bgcolor: COLOR_HEADER,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="טמפלייטים/ שם / טקסט"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    component="span"
                    sx={{
                      width: 24,
                      height: 24,
                      display: "inline-block",
                      backgroundImage: SEARCH_ICON_BG,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      backgroundSize: "contain",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
                color: "#FFFFFF",
                bgcolor: COLOR_HEADER,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(173, 180, 189, 0.45)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLOR_ACCENT,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLOR_ACCENT,
                },
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255,255,255,0.7)",
                fontSize: "12px",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: COLOR_ACCENT,
              },
            }}
          />
          <IconButton
            aria-label="Refresh"
            onClick={refreshTemplates}
            sx={{ color: "#FFFFFF" }}
          >
            <RefreshIcon sx={{ color: "#FFFFFF" }} />
          </IconButton>
        </Box>

        {/* Content grid */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: "14px 18px",
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "12px",
            alignContent: "start",
            gridAutoRows: "1fr",
            bgcolor: COLOR_SURFACE,
          }}
        >
          {filtered.length ? (
            filtered.map((item) => {
              filtered[5].ApprovalStatus = 3;
              const isSelected = selectedTpl?.TemplateId === item.TemplateId;
              const clickable = item.ApprovalStatus === 1;

              return (
                <Box
                  key={item.TemplateId}
                  onClick={() => clickable && setSelectedTpl(item)}
                  sx={{
                    border: isSelected
                      ? `2px solid ${COLOR_ACCENT}`
                      : "1px solid rgba(173, 180, 189, 0.35)",
                    borderRadius: "6px",
                    bgcolor: "#1E1E1E",
                    minHeight: 182,
                    width: "100%",
                    cursor: clickable ? "pointer" : "not-allowed",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s",
                    overflow: "hidden",
                    opacity: clickable ? 1 : 0.6,
                    filter: clickable ? "none" : "grayscale(30%)",
                    position: "relative",
                    "&:hover": {
                      borderColor: clickable ? COLOR_ACCENT : "rgba(173, 180, 189, 0.35)",
                    },
                  }}
                >
                  {!clickable && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        zIndex: 1,
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {/* Title - Fixed height for all titles */}
                  <Box
                    sx={{
                      minHeight: 30,
                      maxHeight: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1,
                      borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
                      flexShrink: 0,
                      bgcolor: "#1E1E1E",
                    }}
                  >
                    <Tooltip title={item.TemplateName || ""} placement="top">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "12px",
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "180px",
                        }}
                      >
                        {item.TemplateName}
                      </Typography>
                    </Tooltip>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: statusColor(item.ApprovalStatus),
                          border: "1px solid rgba(255,255,255,0.25)",
                        }}
                      />
                      {isSelected && (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: COLOR_ACCENT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 10,
                              height: 8,
                              backgroundImage:
                                "url(\"data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3.66667L3.66667 6.33333L9 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                              backgroundSize: "contain",
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Message Content - Scrollable */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: "12px 10px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: "100%",
                        textAlign: "center",
                        wordBreak: "break-word",
                        color: "#FFFFFF",
                        fontSize: "14px",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.MessageText}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography>No Templates Found</Typography>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: "20px",
            pb: "16px",
            pt: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            bgcolor: COLOR_SURFACE,
            borderTop: "1px solid rgba(173, 180, 189, 0.2)",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              width: "187px",
              height: "50px",
              minWidth: "187px",
              borderRadius: "7px",
              borderColor: COLOR_ACCENT,
              textTransform: "none",
              color: "#FFFFFF",
              "&:hover": {
                borderColor: COLOR_ACCENT,
                bgcolor: "rgba(13, 163, 226, 0.08)",
              },
            }}
            onClick={setClosed}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontFamily: "Assistant, sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                lineHeight: 1,
              }}
            >
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            disabled={!selectedTpl}
            sx={{
              width: "187px",
              height: "50px",
              minWidth: "187px",
              borderRadius: "7px",
              textTransform: "none",
              backgroundColor: COLOR_ACCENT,
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: COLOR_ACCENT,
                opacity: 0.92,
              },
            }}
            onClick={handleSubmit}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontFamily: "Assistant, sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                lineHeight: 1,
              }}
            >
              Submit
            </Typography>
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
